import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import { useNotify, useRefresh } from 'react-admin';

import supplyPost from '../controls/supplyApi';

const BUCKETS = [
    { id: 'QA_PENDING', name: 'QA Pending' },
    { id: 'RESERVE', name: 'Reserve' },
    { id: 'RELEASE', name: 'Release' },
    { id: 'TRANSFER', name: 'Transfer' },
    { id: 'HOLD', name: 'Hold' },
];

/**
 * One dialog covers every manual movement. `mode` decides which fields show:
 *   adjust      - pick the adjustment type (QA approve, release, consume, ...)
 *   hold        - move a quantity out of a chosen bucket into Hold
 *   releasehold - move a quantity out of Hold back into a chosen bucket
 */
const ADJ_TYPES = [
    { id: 'QA_APPROVE', name: 'QA Approve (QA Pending to Reserve)' },
    { id: 'QA_REJECT', name: 'QA Reject (QA Pending to Hold)' },
    { id: 'RELEASE', name: 'Release (Reserve to Release)' },
    { id: 'CONSUMED', name: 'Consume (out of Release)' },
    { id: 'TRANSFER_OUT', name: 'Transfer Out (Release to Transfer)' },
    { id: 'TRANSFER_CONFIRM', name: 'Confirm Transfer (out of Transfer)' },
    { id: 'CORRECTION', name: 'Correction' },
];

const AdjustmentDialog = ({ open, onClose, record, mode }) => {
    const notify = useNotify();
    const refresh = useRefresh();

    const [adjType, setAdjType] = useState('QA_APPROVE');
    const [qty, setQty] = useState(1);
    const [fromStatus, setFromStatus] = useState('RELEASE');
    const [toStatus, setToStatus] = useState('RESERVE');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    if (!record) { return null; }

    const titles = {
        adjust: 'New Adjustment',
        hold: 'Apply Hold',
        releasehold: 'Release Hold',
    };

    const needsReason = mode !== 'adjust'
        || ['QA_REJECT', 'TRANSFER_OUT', 'CORRECTION'].indexOf(adjType) > -1;

    const handleSubmit = () => {
        if (!qty || Number(qty) <= 0) {
            notify('Quantity must be greater than zero', 'warning');
            return;
        }
        if (needsReason && !reason) {
            notify('A reason is required', 'warning');
            return;
        }

        let path;
        let body;

        if (mode === 'hold') {
            path = `supplyreceipts/${record.id}/hold`;
            body = { qty: Number(qty), from_status: fromStatus, reason };
        } else if (mode === 'releasehold') {
            path = `supplyreceipts/${record.id}/releasehold`;
            body = { qty: Number(qty), to_status: toStatus, reason };
        } else {
            path = `supplyreceipts/${record.id}/adjust`;
            body = {
                adj_type: adjType,
                qty: Number(qty),
                from_status: fromStatus,
                to_status: toStatus,
                reason,
            };
        }

        setSaving(true);
        supplyPost(path, body)
            .then(() => {
                setSaving(false);
                notify('Adjustment recorded');
                onClose();
                refresh();
            })
            .catch(error => {
                setSaving(false);
                notify(
                    (error && error.body) || (error && error.message) || 'Could not record the adjustment',
                    'warning'
                );
            });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{titles[mode] || 'New Adjustment'}</DialogTitle>
            <DialogContent>
                {mode === 'adjust' && (
                    <TextField
                        select
                        fullWidth
                        margin="dense"
                        label="Adjustment Type"
                        value={adjType}
                        onChange={e => setAdjType(e.target.value)}
                    >
                        {ADJ_TYPES.map(t => (
                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                    </TextField>
                )}

                {(mode === 'hold' || (mode === 'adjust' && adjType === 'CORRECTION')) && (
                    <TextField
                        select
                        fullWidth
                        margin="dense"
                        label="From Status"
                        value={fromStatus}
                        onChange={e => setFromStatus(e.target.value)}
                    >
                        {BUCKETS.map(b => (
                            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                        ))}
                    </TextField>
                )}

                {(mode === 'releasehold' || (mode === 'adjust' && adjType === 'CORRECTION')) && (
                    <TextField
                        select
                        fullWidth
                        margin="dense"
                        label="To Status"
                        value={toStatus}
                        onChange={e => setToStatus(e.target.value)}
                    >
                        {BUCKETS.map(b => (
                            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                        ))}
                    </TextField>
                )}

                <TextField
                    fullWidth
                    margin="dense"
                    type="number"
                    label="Quantity"
                    value={qty}
                    onChange={e => setQty(e.target.value)}
                />

                <TextField
                    fullWidth
                    multiline
                    margin="dense"
                    label={needsReason ? 'Reason (required)' : 'Reason'}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSubmit} color="primary" variant="contained" disabled={saving}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdjustmentDialog;
